import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, DollarSign, Calendar, Percent, Clock, Shield, TrendingUp, Users, BookOpen, MessageCircle } from 'lucide-react';

const FastBusinessCash = () => {
  const fundingDetails = [
    {
      icon: <DollarSign className="w-12 h-12 text-accent-500" />,
      title: "Funding Amount",
      value: "£1,000 – £25,000",
      description: "Businesses can access funding amounts designed for short-term operational needs",
    },
    {
      icon: <Calendar className="w-12 h-12 text-accent-500" />,
      title: "Loan Period",
      value: "3 – 10 Months",
      description: "Fast Business Cash loans are typically structured as short-term funding solutions",
    },
    {
      icon: <Percent className="w-12 h-12 text-accent-500" />,
      title: "Typical Cost",
      value: "20% – 60% APR",
      description: "Rates vary depending on business circumstances, credit profile, and repayment capacity",
    },
  ];

  const features = [
    {
      icon: <Clock className="w-8 h-8 text-primary-600" />,
      title: "Short-Term Funding",
      description: "Fast Business Cash is commonly used to cover short-term funding requirements such as temporary cash flow gaps",
    },
    {
      icon: <Shield className="w-8 h-8 text-primary-600" />,
      title: "Unsecured Finance",
      description: "This type of funding is often unsecured, meaning businesses may not need to provide assets as collateral",
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-primary-600" />,
      title: "Flexible Credit Criteria",
      description: "Businesses with less than perfect credit may still qualify depending on their ability to repay the loan",
    },
    {
      icon: <Users className="w-8 h-8 text-primary-600" />,
      title: "Suitable for Newer Businesses",
      description: "Fast Business Cash may be available even for relatively new businesses with growing revenue",
    },
  ];

  const useCases = [
    "Managing seasonal cash flow fluctuations",
    "Paying unexpected bills",
    "Purchasing inventory",
    "Covering short-term operational expenses",
    "Taking advantage of growth opportunities",
  ];

  const resources = [
    {
      icon: <BookOpen className="w-8 h-8 text-primary-600" />,
      title: "SME Funding Guide",
      description: "Learn about different funding options available to small and medium-sized businesses",
      button: "Read More",
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-primary-600" />,
      title: "Case Studies",
      description: "Discover how businesses have successfully secured funding through 1ST TRUST FINANCES",
      button: "View Case Studies",
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-primary-600" />,
      title: "Need Help?",
      description: "Speak with our funding specialists to explore the best funding solutions for your business",
      button: "Contact Us",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                What is{' '}
                <span className="gradient-text">Fast Business Cash</span>?
              </h1>
              <p className="text-xl text-gray-600 font-medium">
                A fast and convenient way to inject cash into your business when you need it most.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Fast Business Cash is a short-term funding solution designed to provide businesses with quick access to working capital.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At 1ST TRUST FINANCES, we help businesses access flexible funding that can be used to cover unexpected expenses, seasonal demand, or short-term growth opportunities.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Fast Business Cash provides a simple way for businesses to secure funding quickly without lengthy approval processes.
              </p>
              <Link to="/apply-for-funding" className="btn-primary inline-flex items-center">
                Apply for Funding
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="fastCashGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#fastCashGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="100" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="190" width="160" height="40" rx="5" fill="#0056d6" />
                  <circle cx="200" cy="100" r="40" fill="#0ea5e9" opacity="0.2" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="overviewGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#overviewGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="150" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="150" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="200" width="200" height="80" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="230" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Fast Business Cash Loans for Short-Term Business Needs
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Fast Business Cash loans are designed to provide quick funding for short-term financial needs.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Businesses often require immediate funding to cover unexpected expenses, purchase inventory, or manage seasonal fluctuations in cash flow.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This type of funding allows companies to access capital quickly and repay it over a short period, making it an ideal option for businesses that need fast and flexible financing.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Funding Details Section */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Funding Details
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Clear and transparent funding terms for your business.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {fundingDetails.map((detail, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{detail.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-2">
                  {detail.title}
                </h3>
                <p className="text-2xl font-bold text-accent-600 mb-4">
                  {detail.value}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {detail.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fast Business Cash At a Glance */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Fast Business Cash at a Glance
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Key features and benefits of Fast Business Cash.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="font-heading text-lg font-bold text-primary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                When Businesses Use Fast Business Cash
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Businesses often use Fast Business Cash to solve short-term financial challenges or take advantage of new opportunities.
              </p>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-semibold text-primary-900 mb-4">Common situations include:</h4>
                <ul className="space-y-3">
                  {useCases.map((useCase, index) => (
                    <li key={index} className="flex items-start text-gray-600">
                      <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                      {useCase}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Fast Business Cash can help businesses maintain stability while continuing to grow.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="useCaseGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#useCaseGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="100" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="210" width="160" height="30" rx="5" fill="#0056d6" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Resources
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Helpful resources to guide your funding journey.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{resource.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {resource.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {resource.description}
                </p>
                <button className="btn-secondary w-full">
                  {resource.button}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section gradient-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 1000" className="w-full h-full">
            <defs>
              <pattern id="grid2" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid2)" />
          </svg>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Move Forward With Your Business Funding?
            </h2>
            <p className="text-xl text-gray-300 mb-4">
              1ST TRUST FINANCES helps businesses access funding quickly by connecting them with lenders offering flexible financial solutions.
            </p>
            <p className="text-gray-300 mb-8">
              Get personalised funding options within minutes and choose the best offer for your business.
            </p>
            <Link to="/apply-for-funding" className="inline-block bg-white text-primary-600 font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
              Get Funded Now
              <ArrowRight className="inline ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FastBusinessCash;
